/**
 * TMSContextCard — Inline card for gathering TMS integration preferences
 * before launching a testing session.
 *
 * Two sections:
 *   1. TMS Integration — How should results be tracked?
 *   2. Testing Scope — What should be tested?
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Button,
  Chip,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  alpha,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Storage as TrackIcon,
  FolderOpen as ExistingIcon,
  Add as EnrichIcon,
  Speed as IndependentIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  TMSContextPrompt,
  TMSContextOption,
  TMSExistingPlan,
  TMSExistingTestCase,
} from '../types';

interface TMSContextCardProps {
  prompt: TMSContextPrompt;
  onSelect: (
    choice: string,
    planId?: number,
    scope?: string,
    selectedTestCaseIds?: number[],
  ) => void;
  disabled?: boolean;
}

const OPTION_ICONS: Record<string, React.ReactNode> = {
  independent: <IndependentIcon />,
  track_in_tms: <TrackIcon />,
  use_existing_plan: <ExistingIcon />,
  enrich_existing: <EnrichIcon />,
};

const TMSContextCard: React.FC<TMSContextCardProps> = ({
  prompt,
  onSelect,
  disabled = false,
}) => {
  const [selectedTmsOption, setSelectedTmsOption] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [selectedScope, setSelectedScope] = useState<string | null>(null);
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState<number[]>([]);
  const [testCaseSearch, setTestCaseSearch] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isDisabled = disabled || submitted;

  const filteredTestCases = testCaseSearch
    ? prompt.existing_test_cases.filter(
        (tc) => {
          const q = testCaseSearch.toLowerCase();
          return (
            tc.title.toLowerCase().includes(q) ||
            (tc.module && tc.module.toLowerCase().includes(q))
          );
        },
      )
    : prompt.existing_test_cases;

  const needsPlanSelection =
    selectedTmsOption === 'use_existing_plan' || selectedTmsOption === 'enrich_existing';

  const canSubmit =
    selectedTmsOption !== null &&
    selectedScope !== null &&
    (!needsPlanSelection || selectedPlanId !== null) &&
    (selectedScope !== 'selected' || selectedTestCaseIds.length > 0 || prompt.existing_test_cases.length === 0);

  const handleTmsSelect = (id: string) => {
    if (isDisabled) return;
    setSelectedTmsOption(id);
    // Reset plan selection when switching away from plan-requiring options
    if (id !== 'use_existing_plan' && id !== 'enrich_existing') {
      setSelectedPlanId(null);
    }
  };

  const handleScopeSelect = (id: string) => {
    if (isDisabled) return;
    setSelectedScope(id);
    if (id !== 'selected') {
      setSelectedTestCaseIds([]);
    }
  };

  const handleTestCaseToggle = (id: number) => {
    if (isDisabled) return;
    setSelectedTestCaseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = () => {
    if (!canSubmit || isDisabled) return;
    setSubmitted(true);
    onSelect(
      selectedTmsOption!,
      selectedPlanId ?? undefined,
      selectedScope ?? undefined,
      selectedScope === 'selected' ? selectedTestCaseIds : undefined,
    );
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {prompt.operation === 'api_testing'
          ? 'Configure API Testing Session'
          : 'Configure Testing Session'}
      </Typography>

      {/* Section 1: TMS Integration */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        How should results be tracked?
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
          mb: 2,
        }}
      >
        {prompt.tms_options.map((option: TMSContextOption) => {
          const needsPlan = option.id === 'use_existing_plan' || option.id === 'enrich_existing';
          const optionDisabled = isDisabled || (needsPlan && prompt.existing_plans.length === 0);
          return (
          <Card
            key={option.id}
            variant="outlined"
            sx={{
              borderColor:
                selectedTmsOption === option.id ? 'primary.main' : 'divider',
              borderWidth: selectedTmsOption === option.id ? 2 : 1,
              bgcolor:
                selectedTmsOption === option.id
                  ? (theme) => alpha(theme.palette.primary.main, 0.04)
                  : 'background.paper',
              opacity: optionDisabled && !isDisabled ? 0.5 : 1,
              transition: 'all 0.15s ease',
            }}
          >
            <CardActionArea
              onClick={() => handleTmsSelect(option.id)}
              disabled={optionDisabled}
              sx={{ p: 1.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: selectedTmsOption === option.id ? 'primary.main' : 'text.secondary' }}>
                  {OPTION_ICONS[option.id] || <TrackIcon />}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            </CardActionArea>
          </Card>
          );
        })}
      </Box>

      {/* Plan selector (when use_existing_plan or enrich_existing) */}
      {needsPlanSelection && prompt.existing_plans.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Select Test Plan</InputLabel>
          <Select
            value={selectedPlanId ?? ''}
            label="Select Test Plan"
            onChange={(e) => setSelectedPlanId(Number(e.target.value))}
            disabled={isDisabled}
          >
            {prompt.existing_plans.map((plan: TMSExistingPlan) => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name}
                {plan.status && (
                  <Chip
                    label={plan.status}
                    size="small"
                    sx={{ ml: 1 }}
                    variant="outlined"
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {needsPlanSelection && prompt.existing_plans.length === 0 && !isDisabled && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          No existing test plans found — choose "Run Independently" or "Track in TMS" instead.
        </Typography>
      )}

      {/* Section 2: Testing Scope */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        What should be tested?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {prompt.scope_options.map((option: TMSContextOption) => {
          const scopeDisabled = isDisabled;
          return (
          <Card
            key={option.id}
            variant="outlined"
            sx={{
              flex: '1 1 auto',
              minWidth: 180,
              borderColor:
                selectedScope === option.id ? 'primary.main' : 'divider',
              borderWidth: selectedScope === option.id ? 2 : 1,
              bgcolor:
                selectedScope === option.id
                  ? (theme) => alpha(theme.palette.primary.main, 0.04)
                  : 'background.paper',
              opacity: 1,
              transition: 'all 0.15s ease',
            }}
          >
            <CardActionArea
              onClick={() => handleScopeSelect(option.id)}
              disabled={scopeDisabled}
              sx={{ p: 1.5 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {option.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            </CardActionArea>
          </Card>
          );
        })}
      </Box>

      {/* Test case selector (when scope is "selected") */}
      {selectedScope === 'selected' && prompt.existing_test_cases.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search test cases..."
              value={testCaseSearch}
              onChange={(e) => setTestCaseSearch(e.target.value)}
              disabled={isDisabled}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Chip
              label={`${filteredTestCases.length} of ${prompt.existing_test_cases.length}`}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <List dense disablePadding>
              {filteredTestCases.map((tc: TMSExistingTestCase) => (
                <ListItem
                  key={tc.id}
                  component="label"
                  sx={{
                    cursor: isDisabled ? 'default' : 'pointer',
                    '&:hover': isDisabled ? {} : { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedTestCaseIds.includes(tc.id)}
                      onChange={() => handleTestCaseToggle(tc.id)}
                      disabled={isDisabled}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={tc.title}
                    secondary={
                      [tc.module, tc.priority].filter(Boolean).join(' · ') || undefined
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </>
      )}
      {selectedScope === 'selected' && prompt.existing_test_cases.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          No test cases found in this project. Choose "All Pages" or "All APIs" instead.
        </Typography>
      )}

      {/* Submit button */}
      <Button
        variant="contained"
        startIcon={<StartIcon />}
        onClick={handleSubmit}
        disabled={!canSubmit || isDisabled}
        size="medium"
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        Start Testing
      </Button>
    </Box>
  );
};

export default TMSContextCard;
